/** Contexte d'authentification adossé à l'authStore PocketBase. */

import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import type { UserRecord } from '@/types/records';
import { pb } from './pocketbase';

interface AuthContextValue {
  user: UserRecord | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserRecord | null>(
    (pb.authStore.record as UserRecord | null) ?? null,
  );

  // Synchronise l'état React avec l'authStore (login/logout/refresh token).
  useEffect(() => {
    return pb.authStore.onChange(() => {
      setUser((pb.authStore.record as UserRecord | null) ?? null);
    });
  }, []);

  async function login(email: string, password: string) {
    await pb.collection('users').authWithPassword(email, password);
  }

  async function register(email: string, password: string) {
    await pb.collection('users').create({
      email,
      password,
      passwordConfirm: password,
    });
    await login(email, password);
  }

  function logout() {
    pb.authStore.clear();
    // Purge cache React Query + caches du service worker : sinon les données
    // personnelles survivraient à la déconnexion (fuite inter-comptes, appareil partagé).
    queryClient.clear();
    navigator.serviceWorker?.controller?.postMessage('clear-caches');
  }

  // Suppression DÉFINITIVE du compte : le delete côté serveur efface toutes les
  // données par cascadeDelete. Le teardown est APRÈS le await (échec réseau →
  // session et données intactes). Le SDK vide déjà l'authStore sur self-delete ;
  // on le refait par sécurité, puis on purge les caches comme au logout.
  async function deleteAccount() {
    const id = pb.authStore.record?.id;
    if (!id) return;
    await pb.collection('users').delete(id);
    pb.authStore.clear();
    queryClient.clear();
    navigator.serviceWorker?.controller?.postMessage('clear-caches');
  }

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, login, register, logout, deleteAccount }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un AuthProvider');
  return ctx;
}
